import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartContainer, type ChartConfig } from '@/shared/components/ui/chart';
import { getEfficiencyLevel } from './PerformanceCellBadge';
import { EFFICIENCY_LEVEL } from '../types';
import type { MemberTableEntry } from '../types';

const LEVEL_FILL: Record<string, string> = {
  [EFFICIENCY_LEVEL.OPTIMAL]: '#22c55e',
  [EFFICIENCY_LEVEL.GOOD]: '#3b82f6',
  [EFFICIENCY_LEVEL.REGULAR]: '#f59e0b',
  [EFFICIENCY_LEVEL.LOW]: '#ef4444',
};

const chartConfig = {
  efficiency: { label: 'Eficiencia' },
} satisfies ChartConfig;

interface EfficiencyBarChartProps {
  entries: MemberTableEntry[];
}

export function EfficiencyBarChart({ entries }: EfficiencyBarChartProps) {
  const { t } = useTranslation('laborPerformance');

  const data = entries.map((e) => ({
    name: e.memberName.split(' ')[0],
    efficiency: e.efficiency,
    memberId: e.memberId,
  }));

  return (
    <div
      className="rounded-lg border bg-card p-4"
      data-testid="labor-performance-efficiency-bar-chart"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">{t('chart.efficiencyTitle')}</span>
        <span className="text-xs text-muted-foreground">{t('chart.efficiencySubtitle')}</span>
      </div>
      <ChartContainer config={chartConfig} className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 'dataMax']}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11 }}
            />
            <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v}%`, t('chart.efficiencySubtitle')]} />
            <Bar dataKey="efficiency" radius={[0, 4, 4, 0]}>
              {data.map((entry) => (
                <Cell
                  key={entry.memberId}
                  fill={LEVEL_FILL[getEfficiencyLevel(entry.efficiency)]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
